import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, CheckCircle2, MessageSquare } from "lucide-react";
import { API, btnStyle } from "./shared";
import { ChatPanel } from "./ChatPanel";

// Admin observer view — browse every active chat thread (buyer <-> supplier /
// agency) with a last-message preview, filters, and a read-only inline reader.
// Admins can read but cannot post (enforced server-side).
export function AdminConversations({ role }) {
  const [threads, setThreads] = useState(null);
  const [scopeType, setScopeType] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [active, setActive] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (scopeType) params.set("scope_type", scopeType);
      if (debouncedQuery) params.set("q", debouncedQuery);
      const { data } = await axios.get(`${API}/admin/chats${params.toString() ? `?${params}` : ""}`);
      setThreads(data.threads || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load conversations");
      setThreads([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopeType, debouncedQuery]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }); }
    catch { return ""; }
  };

  const scopeBadge = (st) => (
    <span style={{background: st === "job" ? "#DBEAFE" : "#FEF3C7", color: st === "job" ? "#1E3A8A" : "#92400E", padding:"2px 10px", borderRadius:999, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em"}}>
      {st === "job" ? "Job" : "Design brief"}
    </span>
  );

  return (
    <section className="content" data-testid="admin-chats">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ADMIN OBSERVER</p>
          <h2>Conversations</h2>
          <p className="subhead">Every active buyer–supplier thread, with last-message preview. Admins can read any conversation to resolve disputes — posting on behalf of a party is blocked.</p>
        </div>
      </div>

      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end",marginBottom:16}}>
        <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase",flex:"1 1 280px"}}>
          Search
          <input
            data-testid="admin-chat-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Message text, title, or participant name…"
            style={{padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,background:"#fff",color:"#111827",fontWeight:500,textTransform:"none",letterSpacing:"normal"}}
          />
        </label>
        <div style={{display:"flex",gap:6}}>
          {[{code:"",label:"All"},{code:"brief",label:"Design briefs"},{code:"job",label:"Jobs"}].map(s => (
            <button
              key={s.code || "all"}
              data-testid={`admin-chat-filter-${s.code || "all"}`}
              onClick={() => setScopeType(s.code)}
              style={{
                background: scopeType === s.code ? "#111827" : "#F3F4F6",
                color: scopeType === s.code ? "#FACC15" : "#374151",
                border: `1px solid ${scopeType === s.code ? "#111827" : "#D1D5DB"}`,
                padding:"7px 14px",borderRadius:999,fontSize:11,fontWeight:600,cursor:"pointer",
              }}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

      <div style={{display:"grid",gridTemplateColumns:"minmax(360px, 420px) 1fr",gap:16,alignItems:"flex-start"}}>
        <div data-testid="admin-chat-list" style={{display:"grid",gap:8,maxHeight:720,overflowY:"auto",paddingRight:4}}>
          {threads === null ? <p style={{color:"#6B7280",fontSize:13}}>Loading…</p> :
            threads.length === 0 ? <p style={{color:"#6B7280",fontSize:13,padding:"24px 0",textAlign:"center"}}>No conversations match your filters.</p> :
            threads.map(t => {
              const isActive = active && active.scope_type === t.scope_type && active.scope_id === t.scope_id;
              return (
                <button
                  key={`${t.scope_type}-${t.scope_id}`}
                  data-testid={`admin-chat-thread-${t.scope_id}`}
                  onClick={() => setActive(t)}
                  style={{
                    textAlign:"left",
                    background: isActive ? "#111827" : "#fff",
                    color: isActive ? "#F9FAFB" : "#111827",
                    border: `1px solid ${isActive ? "#111827" : "#E5E7EB"}`,
                    borderRadius: 12,
                    padding: "12px 14px",
                    cursor: "pointer",
                    display:"grid",
                    gap:6,
                  }}
                >
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                    {scopeBadge(t.scope_type)}
                    <span style={{fontSize:10, color: isActive ? "#CBD5F5" : "#9CA3AF"}}>{fmt(t.last_at)}</span>
                  </div>
                  <div style={{fontSize:13, fontWeight:600, color: isActive ? "#F9FAFB" : "#111827", overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</div>
                  <div style={{fontSize:11, color: isActive ? "#CBD5F5" : "#6B7280"}}>{t.buyer_name || "—"} ↔ {t.counterparty_name || "—"} · {t.message_count} msg{t.message_count === 1 ? "" : "s"}</div>
                  <div style={{fontSize:12, color: isActive ? "#E5E7EB" : "#374151", overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    <b style={{color: isActive ? "#FDE68A" : "#1F2937"}}>{t.last_sender_role === "system" ? "System" : (t.last_sender_name || t.last_sender_role)}:</b> {t.last_message}
                  </div>
                </button>
              );
            })
          }
        </div>
        <div data-testid="admin-chat-reader" style={{position:"sticky",top:12}}>
          {active ? (
            <div style={{border:"1px solid #E5E7EB",borderRadius:12,background:"#F9FAFB",padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:8,flexWrap:"wrap"}}>
                <div>
                  <h4 style={{margin:0,fontSize:15,color:"#111827"}}>{active.title}</h4>
                  <p style={{margin:"3px 0 0",fontSize:11,color:"#6B7280"}}>{active.buyer_name || "—"} ↔ {active.counterparty_name || "—"} · {active.scope_type} · <code style={{fontSize:10}}>{active.scope_id}</code></p>
                </div>
                <button data-testid="admin-chat-close" onClick={() => setActive(null)} style={{background:"transparent",border:"1px solid #D1D5DB",color:"#374151",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>Close</button>
              </div>
              <ChatPanel scopeType={active.scope_type} scopeId={active.scope_id} role={role}/>
            </div>
          ) : (
            <div style={{border:"1px dashed #D1D5DB",borderRadius:12,padding:"40px 24px",background:"#FFFFFF",textAlign:"center",color:"#9CA3AF"}}>
              <MessageSquare size={28} strokeWidth={1.4}/>
              <p style={{margin:"10px 0 0",fontSize:13}}>Pick a thread on the left to read the full conversation.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


export function AcPartnerApps({ onChange }) {
  const [apps, setApps] = useState(null);
  const [filter, setFilter] = useState("new");
  const [busy, setBusy] = useState("");
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/agency-applications${filter ? `?status=${filter}` : ""}`);
      setApps(data.applications || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load applications");
      setApps([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (id, status) => {
    setBusy(id); setErr("");
    try {
      await axios.patch(`${API}/admin/agency-applications/${id}`, { status, admin_note: noteFor === id ? note : null });
      toast.success(`Marked as ${status}`);
      setNoteFor(null); setNote("");
      await load();
      onChange && onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update application");
    } finally { setBusy(""); }
  };

  const statusChips = [
    { code: "new", label: "New" },
    { code: "contacted", label: "Contacted" },
    { code: "approved", label: "Approved" },
    { code: "rejected", label: "Rejected" },
  ];

  const badge = (s) => {
    const map = {
      new: { bg: "#FEF3C7", color: "#92400E" },
      contacted: { bg: "#DBEAFE", color: "#1E3A8A" },
      approved: { bg: "#DCFCE7", color: "#065F46" },
      rejected: { bg: "#FEE2E2", color: "#991B1B" },
    };
    const s0 = map[s] || map.new;
    return <span style={{background:s0.bg,color:s0.color,padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{s}</span>;
  };

  return (
    <div className="ac-panel" data-testid="ac-partner-apps">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:14}}>
        <div>
          <h3 style={{margin:0,fontSize:18}}>Agency applications</h3>
          <p style={{margin:"4px 0 0",fontSize:12,color:"#6B7280"}}>Prospective design agencies who filled the public <strong>Partner with us</strong> form. Approve to onboard, mark contacted during follow-up, or reject.</p>
        </div>
        <div style={{display:"flex",gap:6}}>
          {statusChips.map(s => (
            <button
              key={s.code}
              data-testid={`ac-partner-filter-${s.code}`}
              onClick={() => setFilter(s.code)}
              style={{
                background: filter === s.code ? "#111827" : "#F3F4F6",
                color: filter === s.code ? "#FACC15" : "#374151",
                border: `1px solid ${filter === s.code ? "#111827" : "#D1D5DB"}`,
                padding:"6px 12px",borderRadius:999,fontSize:11,fontWeight:600,cursor:"pointer",
              }}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}
      {apps === null ? <p style={{color:"#6B7280",fontSize:13}}>Loading applications…</p> : apps.length === 0 ? (
        <p style={{color:"#6B7280",fontSize:13,padding:"24px 0",textAlign:"center"}}>No {filter} applications.</p>
      ) : (
        <div style={{display:"grid",gap:12}}>
          {apps.map(a => (
            <div key={a.id} data-testid={`partner-app-${a.id}`} style={{border:"1px solid #E5E7EB",borderRadius:12,padding:"16px 18px",background:"#fff"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:10,flexWrap:"wrap"}}>
                <div>
                  <h4 style={{margin:"0 0 4px",fontSize:16,color:"#111827"}}>{a.studio_name}</h4>
                  <p style={{margin:0,fontSize:12,color:"#6B7280"}}>{a.contact_person} · {a.email} · {a.phone}</p>
                  <p style={{margin:"4px 0 0",fontSize:12,color:"#6B7280"}}>{[a.city, a.state].filter(Boolean).join(", ") || "Location not provided"} · Team {a.team_size || "—"} · {a.years_experience != null ? `${a.years_experience} yrs` : "exp n/a"}</p>
                </div>
                <div style={{textAlign:"right"}}>
                  {badge(a.status)}
                  <p style={{margin:"6px 0 0",fontSize:11,color:"#9CA3AF"}}>{new Date(a.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
                </div>
              </div>
              {(a.specialties || []).length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                  {a.specialties.map(c => <span key={c} style={{background:"#F3F4F6",color:"#374151",padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:600}}>{c.replace(/_/g," ")}</span>)}
                </div>
              )}
              <p style={{margin:"0 0 10px",fontSize:13,color:"#374151",lineHeight:1.55}}>{a.about}</p>
              {a.portfolio_url && (
                <p style={{margin:"0 0 10px",fontSize:12}}>Portfolio: <a href={a.portfolio_url} target="_blank" rel="noreferrer" style={{color:"#2563EB"}}>{a.portfolio_url}</a></p>
              )}
              {a.admin_note && <p style={{margin:"0 0 10px",fontSize:12,color:"#92400E",background:"#FEF3C7",padding:"6px 10px",borderRadius:6}}>Admin note: {a.admin_note}</p>}

              {noteFor === a.id && (
                <textarea
                  data-testid={`partner-app-note-${a.id}`}
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={2}
                  placeholder="Optional internal note (visible to admins only)…"
                  style={{width:"100%",padding:"8px 10px",border:"1px solid #D1D5DB",borderRadius:8,fontSize:12,fontFamily:"inherit",marginBottom:8}}
                />
              )}

              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button data-testid={`partner-app-contact-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "contacted")} style={btnStyle("#DBEAFE","#1E3A8A")}>Mark contacted</button>
                <button data-testid={`partner-app-approve-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "approved")} style={btnStyle("#DCFCE7","#065F46")}>Approve</button>
                <button data-testid={`partner-app-reject-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "rejected")} style={btnStyle("#FEE2E2","#991B1B")}>Reject</button>
                <button
                  data-testid={`partner-app-note-toggle-${a.id}`}
                  onClick={() => { setNoteFor(noteFor === a.id ? null : a.id); setNote(a.admin_note || ""); }}
                  style={btnStyle("#F3F4F6","#374151")}
                >{noteFor === a.id ? "Hide note" : "Add note"}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AcKycQueue({ onChange }) {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await axios.get(`${API}/admin/kyc/pending`); setUsers(data.users); }
    catch { setUsers([]); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    setBusy(id); setErr("");
    try {
      await axios.post(`${API}/admin/users/${id}/kyc/${action}`, { note: notes[id] || null });
      setNotes(n => ({ ...n, [id]: "" }));
      await load();
      onChange?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update KYC.");
    } finally { setBusy(""); }
  };

  if (!users) return <div className="ac-panel" data-testid="ac-kyc-loading">Loading…</div>;
  if (users.length === 0) return <div className="ac-panel empty" data-testid="ac-kyc-empty">🎉 No pending KYC applications. Everyone's cleared.</div>;

  return (
    <div className="ac-panel" data-testid="ac-kyc">
      <h4 className="ac-sub">{users.length} pending applications</h4>
      <div className="ac-kyc-list">
        {users.map(u => (
          <article key={u.id} className="ac-kyc-card" data-testid={`ac-kyc-row-${u.id}`}>
            <header>
              <div>
                <b>{u.name || "Unnamed"}</b>
                <span className={`chip role-${u.role}`}>{u.role}</span>
                <span className="muted">{u.email}</span>
              </div>
              <span className="muted small">
                {u.created_at ? `Joined ${new Date(u.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}` : ""}
                {u.verification_stage ? ` · ${u.verification_stage}` : ""}
              </span>
            </header>
            <div className="ac-kyc-meta">
              {u.company && <span><b>Company:</b> {u.company}</span>}
              {u.phone && <span><b>Phone:</b> {u.phone}</span>}
              {u.location?.city && <span><b>City:</b> {u.location.city}</span>}
              {u.machine_capabilities?.length ? <span><b>Capabilities:</b> {u.machine_capabilities.join(", ")}</span> : null}
            </div>
            <div className="ac-kyc-actions">
              <input
                data-testid={`ac-kyc-note-${u.id}`}
                placeholder="Optional note (e.g., missing GST doc)"
                value={notes[u.id] || ""}
                onChange={e => setNotes(n => ({ ...n, [u.id]: e.target.value }))}
                disabled={busy === u.id}
              />
              <button data-testid={`ac-kyc-approve-${u.id}`} className="primary-btn" disabled={busy === u.id} onClick={() => decide(u.id, "approve")}>
                {busy === u.id ? "…" : "Approve"} <CheckCircle2 size={14}/>
              </button>
              <button data-testid={`ac-kyc-reject-${u.id}`} className="secondary-btn danger" disabled={busy === u.id} onClick={() => decide(u.id, "reject")}>
                Reject <X size={14}/>
              </button>
            </div>
          </article>
        ))}
      </div>
      {err && <div className="form-error" data-testid="ac-kyc-error">{err}</div>}
    </div>
  );
}


export function AcPendingPayouts({ onChange }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);  // {job_id, supplier_name, payout_inr, ...}

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payouts/pending`);
      setRows(data.jobs || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not load pending payouts"); }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (jobId, payload) => {
    setBusy(jobId); setErr("");
    try {
      await axios.post(`${API}/admin/payouts/${jobId}/mark-paid`, payload);
      setModal(null);
      await load();
      onChange && onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not mark paid.");
    } finally { setBusy(""); }
  };

  if (rows === null) return <div className="ac-panel" data-testid="ac-payouts-loading">Loading…</div>;
  if (rows.length === 0) return <div className="ac-panel empty" data-testid="ac-payouts-empty">No payouts pending right now — every delivered job is settled.</div>;

  return (
    <div className="ac-panel" data-testid="ac-payouts-pending">
      <h4 className="ac-sub">{rows.length} job{rows.length === 1 ? "" : "s"} awaiting manual payout</h4>
      <div className="ac-kyc-list">
        {rows.map(r => {
          const prof = r.supplier?.payout_profile || {};
          const bankReady = !!prof.account_number;
          return (
            <article key={r.job_id} className="ac-kyc-card" data-testid={`ac-payout-row-${r.job_id}`}>
              <div style={{display:"flex",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
                <div style={{minWidth:280,flex:1}}>
                  <b data-testid={`ac-payout-title-${r.job_id}`}>{r.title}</b>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
                    Supplier: <b>{r.supplier?.name}</b> ({r.supplier?.role}) · {r.supplier?.email}
                  </div>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
                    Buyer: {r.buyer?.name || r.buyer?.email || "—"} · Delivered: {r.delivered_at ? new Date(r.delivered_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "pending"}
                  </div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:12,color:"#6b7280"}}>Pay supplier</div>
                  <div style={{fontSize:20,fontWeight:600,color:"#059669"}} data-testid={`ac-payout-amount-${r.job_id}`}>
                    ₹{r.payout_inr?.toLocaleString("en-IN")}
                  </div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>
                    Gross ₹{r.gross_inr?.toLocaleString("en-IN")} − {r.take_rate_pct?.toFixed(1)}% commission
                  </div>
                </div>
              </div>

              <div className="ac-kyc-meta" style={{marginTop:10,background:bankReady ? "#F0FDF4" : "#FEF3C7",padding:10,borderRadius:8}}>
                {bankReady ? (
                  <div style={{fontSize:12,color:"#065F46",fontFamily:"monospace",lineHeight:1.7}} data-testid={`ac-payout-bank-${r.job_id}`}>
                    <b>Beneficiary:</b> {prof.beneficiary_name}<br/>
                    <b>Bank:</b> {prof.bank_name || "—"} · <b>A/C:</b> {prof.account_number} · <b>IFSC:</b> {prof.ifsc_code}<br/>
                    <b>PAN:</b> {prof.pan || "—"} · <b>GSTIN:</b> {prof.gstin || "—"} · <b>Type:</b> {prof.account_type || "savings"}
                  </div>
                ) : (
                  <div style={{fontSize:12,color:"#92400E"}} data-testid={`ac-payout-missing-${r.job_id}`}>
                    ⚠ Supplier hasn't shared bank details yet. Please contact them before releasing.
                  </div>
                )}
              </div>

              <div className="ac-kyc-actions" style={{marginTop:10}}>
                <span className="verify-pill pending" data-testid={`ac-payout-status-${r.job_id}`}>{r.payout_status}</span>
                <button
                  data-testid={`ac-payout-mark-paid-${r.job_id}`}
                  className="primary-btn"
                  disabled={busy === r.job_id || !bankReady}
                  title={bankReady ? "" : "Supplier hasn't saved bank details on their Payouts page yet"}
                  onClick={() => setModal(r)}
                >
                  Mark as paid
                </button>
                {!bankReady && <span data-testid={`ac-payout-no-bank-${r.job_id}`} style={{fontSize:11,color:"#92400E"}}>Awaiting supplier bank details</span>}
              </div>
            </article>
          );
        })}
      </div>
      {err && <div className="form-error" data-testid="ac-payout-error">{err}</div>}

      {modal && <MarkPaidModal row={modal} busy={busy === modal.job_id} onClose={() => setModal(null)} onConfirm={(payload) => markPaid(modal.job_id, payload)}/>}
    </div>
  );
}


export function MarkPaidModal({ row, busy, onClose, onConfirm }) {
  const [utr, setUtr] = useState("");
  const [paidAmount, setPaidAmount] = useState(row.payout_inr || 0);
  const [method, setMethod] = useState("neft");
  const [note, setNote] = useState("");

  const submit = () => {
    if (!utr.trim()) return;
    onConfirm({ utr: utr.trim(), paid_amount_inr: Number(paidAmount), method, note: note || null });
  };

  return (
    <div className="modal-shell" data-testid="mark-paid-modal" onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div onClick={e => e.stopPropagation()} style={{background:"#fff",padding:24,borderRadius:12,width:520,maxWidth:"95vw"}}>
        <h3 style={{margin:0,marginBottom:12}}>Mark payout as paid</h3>
        <p style={{fontSize:13,color:"#6b7280",marginBottom:16}}>
          Record the bank transfer you made to <b>{row.supplier?.name}</b> for job <b>{row.title}</b>.
        </p>

        <div style={{display:"grid",gap:12}}>
          <label style={{fontSize:12,color:"#6b7280"}}>UTR / Transaction reference *
            <input data-testid="mark-paid-utr" value={utr} onChange={e => setUtr(e.target.value)} placeholder="e.g. HDFC2025093001234567" style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}/>
          </label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <label style={{fontSize:12,color:"#6b7280"}}>Amount paid (INR)
              <input data-testid="mark-paid-amount" type="number" value={paidAmount} onChange={e => setPaidAmount(e.target.value)} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}/>
            </label>
            <label style={{fontSize:12,color:"#6b7280"}}>Method
              <select data-testid="mark-paid-method" value={method} onChange={e => setMethod(e.target.value)} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}>
                <option value="neft">NEFT</option>
                <option value="imps">IMPS</option>
                <option value="rtgs">RTGS</option>
                <option value="upi">UPI</option>
                <option value="razorpayx">RazorpayX</option>
              </select>
            </label>
          </div>
          <label style={{fontSize:12,color:"#6b7280"}}>Note (optional)
            <textarea data-testid="mark-paid-note" value={note} onChange={e => setNote(e.target.value)} rows={2} style={{width:"100%",padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,marginTop:4}}/>
          </label>
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
          <button data-testid="mark-paid-cancel" className="secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button data-testid="mark-paid-confirm" className="primary-btn" onClick={submit} disabled={busy || !utr.trim()}>
            {busy ? "Saving…" : "Record payout"}
          </button>
        </div>
      </div>
    </div>
  );
}
